/**
 * LadderPanel - what the opposition knows about the party.
 *
 * The four rungs with the one in force lit, and the line the table reads
 * at it. For everyone it is read-only; whoever directs (`canDirect`) moves
 * the ladder by pressing a rung and may write the line in their own words
 * (saved on leaving the field; blank goes back to the built-in line).
 *
 * At Alarm a card says so, and offers the director the fight.
 *
 * @param {object} props
 * @param {object} props.stealth - `{ state, canDirect, setRung, setLine }` (hooks/useShellStealth)
 * @param {object} [props.fight] - `{ sides, onStart }` for the alarm's Start the fight; absent, none is offered
 */
import { useState } from 'react';
import common from '../../styles/common.module.css';
import story from '../../styles/Story.module.css';
import styles from '../../styles/Stealth.module.css';
import StartFight from '../StartFight';
import { RUNGS, RUNG_LABELS, RUNG_LINES, lineOf } from '../../engine/stealth/ladder';

/** One rung: a button for the director, a word for everyone else. */
function RungCell({ rung, current, onPick }) {
  const here = rung === current;
  const shared = { className: styles.rung, 'data-current': here || undefined, 'data-testid': `rung-${rung}` };
  return (
    <li aria-current={here ? 'step' : undefined}>
      {onPick ? (
        <button type="button" {...shared} aria-pressed={here} onClick={() => onPick(rung)}>
          {RUNG_LABELS[rung]}
        </button>
      ) : (
        <span {...shared}>{RUNG_LABELS[rung]}</span>
      )}
    </li>
  );
}

/**
 * The director's own line for this rung. Set with Enter, the button, or by
 * leaving the field; until then it says it is not saved - a field that saved
 * only on being left looked as though it did nothing (user, 2026-10-05).
 * Each rung keeps its own line: when the ladder moves the field shows that
 * rung's, and coming back finds the one written before.
 */
function LineField({ state, onCommit }) {
  const [draft, setDraft] = useState(state.line);
  // Follow the stored line when it changes under the field: the ladder moved, or another screen wrote it.
  const [seen, setSeen] = useState(state.line);
  if (seen !== state.line) {
    setSeen(state.line);
    setDraft(state.line);
  }
  const pending = draft !== state.line;
  const commit = () => pending && onCommit(draft);
  return (
    <div className={story.field}>
      <label htmlFor="ladder-line-input">Your own line for this rung</label>
      <div className={story.tools}>
        <input
          id="ladder-line-input"
          className={story.input}
          data-testid="ladder-line-input"
          placeholder={RUNG_LINES[state.rung]}
          value={draft}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
          onBlur={commit}
        />
        <button
          type="button"
          className={common.btnSmall}
          data-testid="ladder-line-set"
          disabled={!pending}
          onClick={commit}
        >
          Set
        </button>
      </div>
      {pending && (
        <span className={story.empty} data-testid="ladder-line-unsaved" aria-live="polite">
          Not saved yet - Enter sets it
        </span>
      )}
    </div>
  );
}

/** Alarm: they are seen. The director may start the fight from here. */
function AlarmCard({ canDirect, fight }) {
  return (
    <div className={styles.alarm} role="alert" data-testid="alarm-card">
      <p className={styles.alarmWord}>ALARM</p>
      {canDirect && fight && (
        <div className={story.tools}>
          <StartFight
            sides={fight.sides}
            onStart={fight.onStart}
            className={common.btnPrimary}
            testId="alarm-start-fight"
          />
        </div>
      )}
    </div>
  );
}

export default function LadderPanel({ stealth, fight }) {
  const { state, canDirect } = stealth;
  return (
    <section className={story.section} aria-label="What they know about you" data-testid="ladder-panel">
      <h3 className={story.caption}>What they know about you</h3>
      <ol className={styles.rungs}>
        {RUNGS.map((rung) => (
          <RungCell key={rung} rung={rung} current={state.rung} onPick={canDirect ? stealth.setRung : null} />
        ))}
      </ol>
      {state.rung === 'alarm' && <AlarmCard canDirect={canDirect} fight={fight} />}
      <p className={styles.line} data-testid="ladder-line" aria-live="polite">
        {lineOf(state)}
      </p>
      {canDirect && <LineField state={state} onCommit={stealth.setLine} />}
    </section>
  );
}
